import {
  Directive,
  effect,
  inject,
  input,
  Renderer2,
  resource,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { Nullish } from '@plugins/shared/types';

import { ProfileDataService } from '../services';

@Directive({
  selector: '[pluginsUserProfileLink]',
})
export class UserProfileLinkDirective {
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly templateRef = inject(TemplateRef);
  private readonly renderer = inject(Renderer2);
  private readonly profileDataService = inject(ProfileDataService);

  readonly pluginsUserProfileLink = input<string | Nullish>();
  readonly pluginsUserProfileLinkEnabled = input(true);

  // eslint-disable-next-line @angular-eslint/no-experimental
  private readonly profileUrlResource = resource({
    params: () => ({
      userId: this.pluginsUserProfileLink(),
    }),
    loader: async ({ params }) => {
      if (!params.userId) {
        return null;
      }
      return this.profileDataService.getProfileUrl(params.userId);
    },
  });

  private readonly renderEffect = effect((onCleanup) => {
    const enabled = this.pluginsUserProfileLinkEnabled();
    const userId = this.pluginsUserProfileLink();
    const link = this.profileUrlResource.value();

    this.viewContainerRef.clear();

    const view = this.viewContainerRef.createEmbeddedView(this.templateRef);

    if (!enabled || !link || !userId) {
      return;
    }

    const linkWrapper = this.renderer.createElement('a') as HTMLAnchorElement;

    this.renderer.setAttribute(linkWrapper, 'href', link);
    this.renderer.setAttribute(linkWrapper, 'target', '_blank');
    this.renderer.setAttribute(linkWrapper, 'rel', 'noopener noreferrer');
    this.renderer.setStyle(linkWrapper, 'color', 'currentColor');
    this.renderer.setStyle(linkWrapper, 'text-decoration', 'none');

    const removeClickListener = this.renderer.listen(
      linkWrapper,
      'click',
      (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        // Route through the SDK so each platform can handle the navigation
        // (e.g. open the profile within the native Staffbase app).
        void this.profileDataService.openProfile(userId);
      },
    );
    const removeKeydownListener = this.renderer.listen(
      linkWrapper,
      'keydown',
      (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          void this.profileDataService.openProfile(userId);
        }
      },
    );

    // Move the embedded view's rendered nodes into the anchor wrapper so the
    // entire template content becomes a clickable profile link.
    view.rootNodes.forEach((node) => {
      this.renderer.appendChild(linkWrapper, node);
    });

    const anchor = this.viewContainerRef.element.nativeElement as HTMLElement;
    const parent = anchor.parentNode;

    if (parent) {
      this.renderer.insertBefore(parent, linkWrapper, anchor);
    }

    onCleanup(() => {
      removeClickListener();
      removeKeydownListener();

      if (parent) {
        this.renderer.removeChild(parent, linkWrapper);
      }
    });
  });
}
